/* ──────────────────────────────
   src/pages/LiveUsers.jsx
   ────────────────────────────── */
import React, { useEffect, useState } from "react";

/* libs */
import axios from "axios";
import $ from "jquery";

/* routing */
import { Link } from "react-router-dom";

/* UI / assets */
import Male from "../../assets/images/male.png";
import Pagination from "../../pages/Pagination";
import { usePermission } from "../context/PermissionProvider";

const LiveUsers = () => {
  const { can } = usePermission();
  /* ─── pagination / search state ───────────────────── */
  const [liveData, setLiveData] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("ALL");

  /* ─── query backend ───────────────────────────────── */
  const loadLiveUsers = async (page = 1, limit = 10, keyword = "ALL") => {
    const params = { page, limit, search: keyword };
    const { data } = await axios.get("/liveuser/getLiveUserByAdmin", { params });

    if (data.status) {
      setLiveData(data.liveUser);
      setTotalUsers(data.meta?.totalCount ?? data.liveUser.length);
    }
  };

  /* first load + refresh on paging / search / rows-per-page */
  useEffect(() => {
    loadLiveUsers(activePage, rowsPerPage, search);
  }, [activePage, rowsPerPage, search]);

  /* fallback avatar */
  useEffect(() => {
    $("img").on("error", function () {
      $(this).attr("src", Male);
    });
  }, []);

  /* ─── admin: force-end live ───────────────────────── */
  const endLive = async (liveId) => {
    if (!window.confirm("End this live room now?")) return;

    try {
      await axios.post("/liveuser/liveStreamingCutByAdmin", { liveUserId: liveId });
      loadLiveUsers(activePage, rowsPerPage, search);
      alert("Live room ended");
    } catch (err) {
      alert(err.response?.data?.message || "Error ending live");
    }
  };

  /* ─── admin: set live priority ────────────────────── */
  const setLivePriority = async (liveUserId, priority) => {
    try {
      await axios.post("/liveuser/set-live-priority", {
        liveUserId,
        priority: Number(priority),
      });

      loadLiveUsers(activePage, rowsPerPage, search);
      alert("Priority updated");
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update priority");
    }
  };

  /* reset search & paging */
  const resetFilter = () => {
    setSearch("ALL");
    setActivePage(1);
    setRowsPerPage(10);
  };

  /* ─── JSX ─────────────────────────────────────────── */
  return (
    <>
      {/* header / breadcrumb */}
      <div className="page-title">
        <div className="row">
          <div className="col-12 col-md-6 order-md-1 order-last">
            <h3 className="mb-3 text-dark">Live Users</h3>
          </div>
          <div className="col-12 col-md-6 order-md-2 order-first">
            <nav className="breadcrumb-header float-start float-lg-end">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/admin/dashboard" className="text-danger">
                    Dashboard
                  </Link>
                </li>
                <li className="breadcrumb-item active">Live Users</li>
              </ol>
            </nav>
          </div>
        </div>
      </div>

      {/* card */}
      <div className="row">
        <div className="col">
          <div className="card" id="card">
            {/* filters */}
            <div className="card-header pb-0">
              <div className="row my-3">
                <div className="col-md-8 d-flex align-items-center">
                  <button className="btn btn-info me-3" onClick={resetFilter}>
                    All
                  </button>
                  <span className="badge bg-secondary">
                    {totalUsers} live rooms
                  </span>
                </div>

                {/* search box */}
                <div className="col-md-4">
                  <div className="input-group mb-3 border rounded-pill">
                    <input
                      id="searchBar"
                      type="search"
                      className="form-control bg-none border-0 rounded-pill"
                      placeholder="Search by name or ID…"
                      autoComplete="off"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          const kw = e.target.value.trim() || "ALL";
                          setSearch(kw);
                          setActivePage(1);
                        }
                      }}
                    />
                    <button
                      className="btn text-danger border-0"
                      onClick={() => {
                        const kw =
                          document.getElementById("searchBar").value.trim() ||
                          "ALL";
                        setSearch(kw);
                        setActivePage(1);
                      }}
                    >
                      <i className="fas fa-search" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* table */}
            <div className="card-body card-overflow pt-0">
              <table className="table table-striped mt-5 text-center">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>Image</th>
                    <th>Name</th>
                    <th>UniqueId</th>
                    <th>Gender</th>
                    <th>RCoin</th>
                    <th>Country</th>
                    <th>Level</th>
                    <th>Followers</th>
                    <th>Following</th>
                    <th>Priority</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {liveData.length ? (
                    liveData.map((lu, i) => {
                      const u = lu.liveUserId || {};
                      return (
                        <tr key={lu._id}>
                          <td>
                            {(activePage - 1) * rowsPerPage + i + 1}
                          </td>

                          <td>
                            <img
                              src={u.image || Male}
                              alt="user"
                              height="50"
                              width="50"
                              style={{
                                objectFit: "cover",
                                borderRadius: 10,
                                border: "2px solid #000000",
                              }}
                            />
                          </td>

                          <td>{u.name || "-"}</td>
                          <td>{u.uniqueId || "-"}</td>
                          <td>{u.gender || "-"}</td>
                          <td className="text-danger">{u.rCoin || 0}</td>
                          <td className="text-success">{u.country || "-"}</td>
                          <td className="text-warning">
                            {u.level?.name || "-"}
                          </td>
                          <td>
                            {Array.isArray(u.followers)
                              ? u.followers.length
                              : 0}
                          </td>
                          <td>
                            {Array.isArray(u.following)
                              ? u.following.length
                              : 0}
                          </td>

                          {/* PRIORITY */}
                          <td>
                            {can("admin/liveuser", "Edit") ? (
                              <select
                                className="form-select form-select-sm"
                                style={{ width: 90 }}
                                value={lu.livePriority || 0}
                                onChange={(e) =>
                                  setLivePriority(lu._id, e.target.value)
                                }
                              >
                                <option value={0}>Normal</option>
                                <option value={1}>1</option>
                                <option value={2}>2</option>
                                <option value={3}>3</option>
                                <option value={4}>4</option>
                                <option value={5}>5</option>
                              </select>
                            ) : (
                              <span className="badge bg-info">
                                {lu.livePriority === 0 ? "Normal" : lu.livePriority}
                              </span>
                            )}
                          </td>

                          <td>
                            {can("admin/liveuser", "Edit") ? (
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => endLive(lu._id)}
                              >
                                End&nbsp;Live
                              </button>
                            ) : (
                              <span className="text-muted">No Action</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="12" align="center">
                        No live users found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* pagination */}
              <Pagination
                activePage={activePage}
                rowsPerPage={rowsPerPage}
                userTotal={totalUsers}
                handleRowsPerPage={(v) => {
                  setRowsPerPage(v);
                  setActivePage(1);
                }}
                handlePageChange={(p) => setActivePage(p)}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LiveUsers;
